/**
 * The Reports builder's whole state, and its round trip through the URL (M8 R10).
 * Dates are `YYYY-MM-DD` calendar strings throughout; nothing here builds a `Date` from one
 * except to validate it in UTC, so the browser's zone cannot move a day.
 */
import type { LocalDate } from '../../platform/localDate'
import type { ReportQueryRequest } from '../cashflow/useCashflow'
import { dayMonthYear, MONTH_SHORT, periodTitle, step, unitStart } from '../cashflow/cashflowPeriod'

export type RangePreset = 'mtd' | 'ytd' | '12m' | 'custom'
export type RowsBy = 'category' | 'group' | 'merchant' | 'owner' | 'account' | 'tag'
export type Columns = 'month' | 'quarter' | 'owner'
export type ChartType = 'stacked' | 'grouped' | 'line'
/**
 * Several filters: ANDed across fields, ORed within one. The "none" option of a field (Uncategorised, No merchant,
 * Untagged) sits beside its values and is part of the same field.
 */
export type ReportFilters = {
  groups: string[]
  uncategorised: boolean
  merchants: string[]
  noMerchant: boolean
  accounts: string[]
  owners: string[]
  tags: string[]
  untagged: boolean
  recurring: boolean
}
export const NO_FILTERS: ReportFilters = {
  groups: [],
  uncategorised: false,
  merchants: [],
  noMerchant: false,
  accounts: [],
  owners: [],
  tags: [],
  untagged: false,
  recurring: false,
}
export type ReportSpec = {
  range: RangePreset
  start: LocalDate
  end: LocalDate
  rows: RowsBy
  columns: Columns
  chart: ChartType
  filters: ReportFilters
}

export const DEFAULT_SPEC_PARAMS = { range: 'ytd', rows: 'category', columns: 'month', chart: 'stacked' } as const
/** The server answers 422 past this; the form says so first. */
export const MAX_YEARS = 10

const RANGES: readonly RangePreset[] = ['mtd', 'ytd', '12m', 'custom']
const ROWS: readonly RowsBy[] = ['category', 'group', 'merchant', 'owner', 'account', 'tag']
const COLUMNS: readonly Columns[] = ['month', 'quarter', 'owner']
const CHARTS: readonly ChartType[] = ['stacked', 'grouped', 'line']

function pick<T extends string>(allowed: readonly T[], v: string | null, fallback: T): T {
  return allowed.find((a) => a === v) ?? fallback
}

function validDate(v: string | null): LocalDate | null {
  if (v === null) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (!m) return null
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const t = new Date(Date.UTC(y, mo - 1, d))
  const ok = t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d
  return ok ? v : null
}

/** D1: 12M is the 12 calendar months including the current one. */
export function presetWindow(p: Exclude<RangePreset, 'custom'>, today: LocalDate): { start: LocalDate; end: LocalDate } {
  switch (p) {
    case 'mtd':
      return { start: unitStart(today, 'month'), end: today }
    case 'ytd':
      return { start: `${today.slice(0, 4)}-01-01`, end: today }
    case '12m':
      return { start: step(unitStart(today, 'month'), 'month', -11), end: today }
  }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
/** The server's own cap per field; more is a 422. */
const MAX_PER_FIELD = 50

function ids(params: URLSearchParams, key: string): string[] {
  const valid = params.getAll(key).map((v) => v.toLowerCase()).filter((v) => UUID.test(v))
  return [...new Set(valid)].slice(0, MAX_PER_FIELD).sort()
}

function tags(params: URLSearchParams): string[] {
  const valid = params.getAll('tag').filter((v) => v.trim() !== '')
  return [...new Set(valid)].slice(0, MAX_PER_FIELD).sort()
}

const flag = (params: URLSearchParams, key: string): boolean => params.get(key) === '1'

export function readSpec(params: URLSearchParams, today: LocalDate): ReportSpec {
  const rows = pick(ROWS, params.get('rows'), DEFAULT_SPEC_PARAMS.rows)
  const askedColumns = pick(COLUMNS, params.get('columns'), DEFAULT_SPEC_PARAMS.columns)
  // The server refuses owner rows with owner columns.
  const columns: Columns = rows === 'owner' && askedColumns === 'owner' ? 'month' : askedColumns
  const askedChart = pick(CHARTS, params.get('chart'), DEFAULT_SPEC_PARAMS.chart)
  // Overlapping tags make stacked columns taller than the Total (Q8).
  const chart: ChartType = rows === 'tag' && askedChart === 'stacked' ? 'grouped' : askedChart
  const filters: ReportFilters = {
    groups: ids(params, 'group'),
    uncategorised: flag(params, 'uncategorised'),
    merchants: ids(params, 'merchant'),
    noMerchant: flag(params, 'no_merchant'),
    accounts: ids(params, 'account'),
    owners: ids(params, 'owner'),
    tags: tags(params),
    untagged: flag(params, 'untagged'),
    recurring: flag(params, 'recurring'),
  }

  const asked = pick(RANGES, params.get('range'), DEFAULT_SPEC_PARAMS.range)
  if (asked === 'custom') {
    const start = validDate(params.get('start'))
    const end = validDate(params.get('end'))
    if (start !== null && end !== null && customError(start, end, today) === null) {
      return { range: 'custom', start, end, rows, columns, chart, filters }
    }
    return { range: 'ytd', ...presetWindow('ytd', today), rows, columns, chart, filters }
  }
  return { range: asked, ...presetWindow(asked, today), rows, columns, chart, filters }
}

/** D2: a preset is stored as the preset, so the link moves with time; only a custom range stores dates. */
export function writeSpec(spec: ReportSpec): URLSearchParams {
  const out = new URLSearchParams()
  if (spec.range !== DEFAULT_SPEC_PARAMS.range) out.set('range', spec.range)
  if (spec.range === 'custom') {
    out.set('start', spec.start)
    out.set('end', spec.end)
  }
  if (spec.rows !== DEFAULT_SPEC_PARAMS.rows) out.set('rows', spec.rows)
  if (spec.columns !== DEFAULT_SPEC_PARAMS.columns) out.set('columns', spec.columns)
  if (spec.chart !== DEFAULT_SPEC_PARAMS.chart) out.set('chart', spec.chart)
  const f = spec.filters
  for (const id of [...f.groups].sort()) out.append('group', id)
  if (f.uncategorised) out.set('uncategorised', '1')
  for (const id of [...f.merchants].sort()) out.append('merchant', id)
  if (f.noMerchant) out.set('no_merchant', '1')
  for (const id of [...f.accounts].sort()) out.append('account', id)
  for (const id of [...f.owners].sort()) out.append('owner', id)
  for (const t of [...f.tags].sort()) out.append('tag', t)
  if (f.untagged) out.set('untagged', '1')
  if (f.recurring) out.set('recurring', '1')
  return out
}

/** Deductible spending is the transactions carrying this one tag, matched exactly (tags are free text). */
export const TAX_DEDUCTIBLE_TAG = 'tax-deductible'

export type PresetId = 'category' | 'who_spent_what' | 'subscriptions_creep' | 'tax_deductible'

/** The rail's labels, and the PDF's title when a preset is active. */
export const PRESET_TITLES: Record<PresetId, string> = {
  category: 'Spending by category',
  who_spent_what: 'Who spent what',
  subscriptions_creep: 'Subscriptions creep',
  tax_deductible: 'Tax-deductible',
}

export function presetSpec(id: PresetId, today: LocalDate): ReportSpec {
  switch (id) {
    case 'category':
      return { range: 'ytd', ...presetWindow('ytd', today), rows: 'category', columns: 'month', chart: 'stacked', filters: NO_FILTERS }
    case 'who_spent_what':
      return { range: 'ytd', ...presetWindow('ytd', today), rows: 'category', columns: 'owner', chart: 'stacked', filters: NO_FILTERS }
    case 'tax_deductible':
      return { range: 'ytd', ...presetWindow('ytd', today), rows: 'category', columns: 'month', chart: 'stacked', filters: { ...NO_FILTERS, tags: [TAX_DEDUCTIBLE_TAG] } }
    case 'subscriptions_creep':
      return {
        range: '12m',
        ...presetWindow('12m', today),
        rows: 'merchant',
        columns: 'month',
        chart: 'line',
        filters: { ...NO_FILTERS, recurring: true },
      }
  }
}

/** How many FIELDS carry a filter (0..6): a count of controls, not money. */
export function filterFieldCount(f: ReportFilters): number {
  return [
    f.groups.length > 0 || f.uncategorised,
    f.merchants.length > 0 || f.noMerchant,
    f.accounts.length > 0,
    f.owners.length > 0,
    f.tags.length > 0 || f.untagged,
    f.recurring,
  ].filter(Boolean).length
}

/** A preset is identified by its rows, owner-ness of columns and filters; range, chart and month/quarter are free. */
export function activePreset(spec: ReportSpec): PresetId | null {
  const f = spec.filters
  const ownerColumns = spec.columns === 'owner'
  const onlyRecurring = filterFieldCount(f) === 1 && f.recurring
  const onlyDeductibleTag = filterFieldCount(f) === 1 && !f.untagged && f.tags.length === 1 && f.tags[0] === TAX_DEDUCTIBLE_TAG
  if (spec.rows === 'category' && !ownerColumns && onlyDeductibleTag) return 'tax_deductible'
  if (spec.rows === 'category' && filterFieldCount(f) === 0) return ownerColumns ? 'who_spent_what' : 'category'
  if (spec.rows === 'merchant' && !ownerColumns && onlyRecurring) return 'subscriptions_creep'
  return null
}

export function queryRequest(spec: ReportSpec, tz: string): ReportQueryRequest {
  const f = spec.filters
  return {
    rows: spec.rows,
    columns: spec.columns === 'owner' ? 'owner' : null,
    // Under owner columns the period series is unused, so month keeps the request one shape.
    granularity: spec.columns === 'quarter' ? 'quarter' : 'month',
    start: spec.start,
    end: spec.end,
    tz,
    filters: {
      accountIds: f.accounts,
      ownerIds: f.owners,
      groupIds: f.groups,
      uncategorised: f.uncategorised,
      merchantIds: f.merchants,
      noMerchant: f.noMerchant,
      tags: f.tags,
      untagged: f.untagged,
      recurring: f.recurring,
    },
  }
}

/** The first thing wrong with a typed custom range, or null. The server's 10-year limit is checked here so a 422 is never the first message. */
export function customError(start: string, end: string, today: LocalDate): string | null {
  const s = validDate(start)
  const e = validDate(end)
  if (s === null || e === null) return 'Enter a date.'
  if (s > e) return 'Choose a start date before the end date.'
  if (e > today) return "The end date can't be after today."
  if (e > `${Number(s.slice(0, 4)) + MAX_YEARS}${s.slice(4)}`) return `Choose a range of ${MAX_YEARS} years or less.`
  return null
}

export type FilterNames = {
  group(id: string): string | null
  merchant(id: string): string | null
  account(id: string): string | null
  owner(id: string): string | null
}

/** "A, B or C"; past three, "A, B, C and 2 more tags". A name still loading is counted, never dropped. */
function describe(names: (string | null)[], extra: string[], noun: string): string {
  const known = names.filter((n): n is string => n !== null)
  const unknown = names.length - known.length
  const all = [...known, ...(unknown > 0 ? [`${unknown} ${noun}${unknown === 1 ? '' : 's'}`] : []), ...extra]
  if (all.length <= 3) {
    const head = all.slice(0, -1)
    const last = all[all.length - 1] ?? ''
    return head.length === 0 ? last : `${head.join(', ')} or ${last}`
  }
  return `${all.slice(0, 3).join(', ')} and ${all.length - 3} more ${noun}s`
}

export function subtitle(spec: ReportSpec, names: FilterNames): string {
  const sameYear = spec.start.slice(0, 4) === spec.end.slice(0, 4)
  const from = sameYear ? dayMonthYear(spec.start).replace(/ \d{4}$/, '') : dayMonthYear(spec.start)
  const f = spec.filters
  const fields: string[] = []
  if (f.groups.length > 0 || f.uncategorised) {
    fields.push(describe(f.groups.map(names.group), f.uncategorised ? ['Uncategorised'] : [], 'group'))
  }
  if (f.merchants.length > 0 || f.noMerchant) {
    fields.push(describe(f.merchants.map(names.merchant), f.noMerchant ? ['No merchant'] : [], 'merchant'))
  }
  if (f.accounts.length > 0) fields.push(describe(f.accounts.map(names.account), [], 'account'))
  if (f.owners.length > 0) fields.push(describe(f.owners.map(names.owner), [], 'owner'))
  if (f.tags.length > 0 || f.untagged) fields.push(describe(f.tags, f.untagged ? ['Untagged'] : [], 'tag'))
  if (f.recurring) fields.push('recurring merchants only')
  const parts = [
    `${from} – ${dayMonthYear(spec.end)}`,
    `by ${spec.columns}`,
    ...fields,
    'excludes transfers, pending and excluded transactions',
  ]
  return parts.join(' · ')
}

export function columnLabel(p: { start: LocalDate }, columns: 'month' | 'quarter', spansYears: boolean): string {
  if (columns === 'quarter') return periodTitle(p.start, 'quarter')
  const month = MONTH_SHORT[Number(p.start.slice(5, 7)) - 1] ?? ''
  return spansYears ? `${month} ${p.start.slice(0, 4)}` : month
}
